import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/lib/api'
import type { Campaign } from '@/lib/referral-types'
import { formatCents, formatTs, campaignStatusVariant } from '@/lib/referral-utils'
import { Card } from '@/components/ui/Card'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { Badge } from '@/components/ui/Badge'

export function CampaignListPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['admin-referral-campaigns'],
    queryFn: () => authApi.get<{ campaigns: Campaign[] }>('/api/v1/admin/referral/campaigns'),
  })

  async function handleDelete(e: React.MouseEvent, campaign: Campaign) {
    e.stopPropagation()
    if (!confirm(`Delete "${campaign.name}"? Campaigns with referrals will be archived instead.`)) return
    await authApi.delete(`/api/v1/admin/referral/campaigns/${campaign.id}`)
    queryClient.invalidateQueries({ queryKey: ['admin-referral-campaigns'] })
  }

  const columns: Column<Campaign>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <span className="font-medium">
          {row.name}
          {row.is_default ? <Badge variant="brand" className="ml-2">Default</Badge> : null}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => <Badge variant={campaignStatusVariant(row.status)}>{row.status}</Badge>,
    },
    {
      id: 'referrer_type',
      header: 'Referrer Type',
      cell: (row) => <span className="capitalize">{row.referrer_type}</span>,
    },
    {
      id: 'referred_product',
      header: 'Product',
      cell: (row) => <span className="capitalize">{row.referred_product}</span>,
    },
    {
      id: 'code_count',
      header: 'Codes',
      cell: (row) => <span className="tabular-nums">{row.code_count}</span>,
      className: 'text-right',
    },
    {
      id: 'total_cost_cents',
      header: 'Total Cost',
      cell: (row) => <span className="tabular-nums">{formatCents(row.total_cost_cents)}</span>,
      className: 'text-right',
    },
    {
      id: 'created_at',
      header: 'Created',
      cell: (row) => <span className="text-[var(--content-secondary)] tabular-nums">{formatTs(row.created_at)}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <button
          onClick={(e) => handleDelete(e, row)}
          className="text-xs text-red-500 hover:text-red-600 cursor-pointer"
        >
          Delete
        </button>
      ),
      className: 'text-right w-16',
    },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold">Campaigns</h1>
        <button
          onClick={() => navigate('/referrals/campaigns/new')}
          className="px-4 py-2 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] transition-colors cursor-pointer"
        >
          New Campaign
        </button>
      </div>

      <Card>
        <DataTable
          columns={columns}
          data={data?.campaigns ?? []}
          keyExtractor={(row) => row.id}
          onRowClick={(row) => navigate(`/referrals/campaigns/${row.id}`)}
          loading={isLoading}
          emptyMessage="No campaigns yet"
        />
      </Card>
    </div>
  )
}
